import React, { useState } from "react"
import {Mail, MessageSquare, Send, CheckCircle} from 'lucide-react'

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  })
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission logic here
    setSubmitted(true)
    setTimeout(() => setSubmitted(false), 3000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-3 mb-4">
              <Mail size={40} />
              <h1 className="text-4xl md:text-5xl font-bold">
                Get Expert Advice
              </h1>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have questions about generator auto start systems? Our team of experts is here to help with technical questions, product recommendations, and installation guidance.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              
              {submitted && (
                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded flex items-center">
                  <CheckCircle className="text-green-500 mr-3" size={24} />
                  <p className="text-green-700 font-semibold">
                    Thank you! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select a topic...</option>
                    <option value="installation">Installation Question</option>
                    <option value="product">Product Recommendation</option>
                    <option value="troubleshooting">Troubleshooting Help</option>
                    <option value="solar">Solar Integration</option>
                    <option value="compatibility">Compatibility Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                    placeholder="Please describe your generator model and what you need help with..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Send size={20} />
                  <span>Send Message</span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Additional Resources
              </h2>

              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-orange-500">
                  <div className="flex items-start">
                    <MessageSquare className="text-orange-600 mr-4 mt-1" size={24} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Technical Support
                      </h3>
                      <p className="text-gray-700 text-sm mb-3">
                        For immediate technical questions, check our comprehensive guides section first. Most common installation and troubleshooting issues are covered there.
                      </p>
                      <a href="/guides" className="text-orange-600 hover:text-orange-700 font-semibold text-sm">
                        Browse Guides →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Response Time
                  </h3>
                  <p className="text-gray-700 text-sm mb-4">
                    We typically respond to inquiries within 24 hours during business days. For urgent issues, please include "URGENT" in your subject line.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Business Hours:</strong><br />
                    Monday - Friday: 9:00 AM - 5:00 PM EST
                  </p>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Before Contacting
                  </h3>
                  <p className="text-gray-700 text-sm mb-3">
                    To help us assist you better, please have ready:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Generator brand, model, and serial number</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Current starting system (manual, electric, remote)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Application type (RV, home backup, solar)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>Photos of generator control panel (if applicable)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Common Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <a href="/guides#compatibility" className="bg-white p-5 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow group">
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                Will this work with my generator?
              </h3>
              <p className="text-sm text-gray-600">
                Check our compatibility guides for Honda, Onan, Generac, and Briggs & Stratton models.
              </p>
            </a>

            <a href="/guides#installation" className="bg-white p-5 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow group">
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                How difficult is installation?
              </h3>
              <p className="text-sm text-gray-600">
                Most remote start kits take 1-3 hours with basic electrical knowledge and tools.
              </p>
            </a>

            <a href="/solar-backup-power" className="bg-white p-5 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow group">
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                Can I integrate with solar?
              </h3>
              <p className="text-sm text-gray-600">
                Yes! Learn about 2-wire auto start systems for Victron and Growatt inverters.
              </p>
            </a>

            <a href="/guides#troubleshooting" className="bg-white p-5 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow group">
              <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                Generator won't start remotely?
              </h3>
              <p className="text-sm text-gray-600">
                Follow our troubleshooting guide for diagnosing common remote start failures.
              </p>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactPage